import axios from 'axios';
import { config } from '../config/api';

const API_BASE_URL = config.apiUrl;

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: config.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for auth
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            const response = await this.client.post('/users/auth/refresh/', {
              refresh: refreshToken,
            });
            
            const { access } = response.data;
            localStorage.setItem('access_token', access);
            
            originalRequest.headers.Authorization = `Bearer ${access}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.clear();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async register(userData) {
    const response = await this.client.post('/users/auth/register/', userData);
    if (response.data.tokens) {
      localStorage.setItem('access_token', response.data.tokens.access);
      localStorage.setItem('refresh_token', response.data.tokens.refresh);
    }
    return response.data;
  }

  async login(credentials) {
    const response = await this.client.post('/users/auth/login/', credentials);
    if (response.data.access) {
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
    }
    return response.data;
  }

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }

  // User endpoints
  async getProfile() {
    const response = await this.client.get('/users/me/');
    return response.data;
  }

  // Organizations
  async getOrganizations() {
    const response = await this.client.get('/organizations/');
    return response.data;
  }

  async createOrganization(data) {
    const response = await this.client.post('/organizations/', data);
    return response.data;
  }

  async getOrganization(slug) {
    const response = await this.client.get(`/organizations/${slug}/`);
    return response.data;
  }

  async updateOrganization(slug, data) {
    const response = await this.client.patch(`/organizations/${slug}/`, data);
    return response.data;
  }

  async deleteOrganization(slug) {
    const response = await this.client.delete(`/organizations/${slug}/`);
    return response.data;
  }

  // Organization Members
  async getOrganizationMembers(slug) {
    const response = await this.client.get(`/organizations/${slug}/members/`);
    return response.data;
  }

  async inviteMember(slug, data) {
    const response = await this.client.post(`/organizations/${slug}/members/`, data);
    return response.data;
  }

  async updateMemberRole(slug, memberId, data) {
    const response = await this.client.patch(`/organizations/${slug}/members/${memberId}/`, data);
    return response.data;
  }

  async removeMember(slug, memberId) {
    const response = await this.client.delete(`/organizations/${slug}/members/${memberId}/`);
    return response.data;
  }

  // Sports
  async getSports() {
    const response = await this.client.get('/sports/');
    return response.data;
  }

  // Plans
  async getPlanTemplates(orgSlug) {
    const response = await this.client.get(`/organizations/${orgSlug}/plan-templates/`);
    return response.data;
  }

  async createPlanTemplate(orgSlug, data) {
    const response = await this.client.post(`/organizations/${orgSlug}/plan-templates/`, data);
    return response.data;
  }

  async getPlanTemplate(orgSlug, planId) {
    const response = await this.client.get(`/organizations/${orgSlug}/plan-templates/${planId}/`);
    return response.data;
  }

  async updatePlanTemplate(orgSlug, planId, data) {
    const response = await this.client.patch(`/organizations/${orgSlug}/plan-templates/${planId}/`, data);
    return response.data;
  }

  async deletePlanTemplate(orgSlug, planId) {
    const response = await this.client.delete(`/organizations/${orgSlug}/plan-templates/${planId}/`);
    return response.data;
  }

  async assignPlanToClients(orgSlug, planId, data) {
    const response = await this.client.post(`/organizations/${orgSlug}/plan-templates/${planId}/assign/`, data);
    return response.data;
  }

  async getPlanAssignments(orgSlug, planId) {
    const response = await this.client.get(`/organizations/${orgSlug}/plan-templates/${planId}/assignments/`);
    return response.data;
  }

  // Clients
  async getClients(orgSlug) {
    const response = await this.client.get(`/organizations/${orgSlug}/clients/`);
    return response.data;
  }

  async inviteClient(orgSlug, data) {
    const response = await this.client.post(`/organizations/${orgSlug}/clients/`, data);
    return response.data;
  }

  // Exercise Categories
  async getExerciseCategories() {
    const response = await this.client.get('/exercise-categories/');
    return response.data;
  }

  // Exercises
  async getExercises(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.append('search', params.search);
    if (params.category && params.category !== 'all') searchParams.append('category', params.category);
    if (params.muscle_group) searchParams.append('muscle_group', params.muscle_group);
    if (params.difficulty) searchParams.append('difficulty', params.difficulty);
    if (params.equipment) searchParams.append('equipment', params.equipment);
    
    const queryString = searchParams.toString();
    const url = queryString ? `/exercises/?${queryString}` : '/exercises/';
    const response = await this.client.get(url);
    return response.data;
  }

  async getExercise(slug) {
    const response = await this.client.get(`/exercises/${slug}/`);
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;